import {
  clearError as clearOpError,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { organizationsApi } from '@/api/organizations';
import { auth } from '@/lib/awsCognito.util';
import { invalidateOrgFetches } from './organizationSlice';
import { extractCustomClaims, parseRoles } from '@/lib/jwtUtils.util';

import type { PrivySlice } from './privySlice';
import type { User, ErrorStates, LoadedTimestamps, LoadingStates } from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// GW-677: localStorage key for pending invitation code
const PENDING_INVITATION_CODE_KEY = 'gw-pending-invitation-code';

// GW-749: Recovery info for signup errors (e.g., existing unverified accounts)
export interface SignUpRecovery {
  canResendVerification: boolean;
  email: string;
}

export interface AuthSlice {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: LoadingStates; // Object with keys: checkAuth, signIn, signOut, signUp, confirmSignUp
  hasLoaded: LoadingStates; // Object tracking which operations have completed
  lastLoaded: LoadedTimestamps; // Object tracking timestamps of completed operations
  errors: ErrorStates; // Object tracking errors per operation
  needsOrgSelection: boolean;
  signUpRecovery: SignUpRecovery | null; // GW-749: Recovery info for signup errors
  passwordResetEmail: string | null; // GW-807: Email for password reset flow
  unverifiedEmail: string | null; // GW-8499: Email of an unconfirmed account attempting sign-in

  // Actions
  signIn: (username: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  signUp: (username: string, password: string, email: string) => Promise<boolean>;
  confirmSignUp: (username: string, code: string) => Promise<boolean>;
  resendSignUpCode: (
    username: string
  ) => Promise<boolean | { success: boolean; isAlreadyConfirmed: boolean }>; // GW-805: Return object when user is already confirmed
  forgotPassword: (email: string) => Promise<boolean>; // GW-807: Initiate password reset
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<boolean>; // GW-807: Complete password reset
  checkAuth: () => Promise<void>;
  clearError: (operation?: string) => void; // Clear error for specific operation or all errors
  clearSignUpError: () => void; // GW-749: Clear signUp error AND signUpRecovery (unique name avoids slice collision)
  clearUnverifiedEmail: () => void; // GW-8499: Clear the unverified-email recovery state
  setNeedsOrgSelection: (needs: boolean) => void;
  updateUser: (updates: Partial<User>) => void; // GW-1709: Update user profile in store
  refreshUserFromToken: () => Promise<void>; // GW-2338: Re-fetch JWT after profile save so claims are current
}

const initialAuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: {}, // No operations loading initially
  hasLoaded: {}, // No operations completed initially
  lastLoaded: {}, // No timestamps initially
  errors: {}, // No errors initially
  needsOrgSelection: false,
  signUpRecovery: null, // GW-749: Recovery info for signup errors
  passwordResetEmail: null, // GW-807: Email for password reset flow
  unverifiedEmail: null, // GW-8499: Email of an unconfirmed account attempting sign-in
};

export const createAuthSlice: StateCreator<AuthSlice, [], [], AuthSlice> = (set, get) => ({
  ...initialAuthState,

  signIn: async (username: string, password: string) => {
    const currentState = get();
    // GW-6534/GW-6372: Clear localStorage's stale org id BEFORE we populate
    // any new auth state. The previous flow only cleared Zustand's currentOrg
    // (line 136 below), leaving localStorage holding the previous user's
    // org id. Between signIn and selectOrganization, BaseApiClient would
    // attach an X-Organization-Id header from the wrong user — the GW-6372
    // cross-user contamination root.
    localStorage.removeItem('gw-current-org-id');
    set({
      isLoading: setLoading(currentState.isLoading, 'signIn', true),
      errors: clearOpError(currentState.errors, 'signIn'),
      unverifiedEmail: null, // GW-8499: Clear stale unverified-email recovery state
    });

    const result = await auth.signIn(username, password);

    if (result.success && result.isSignedIn) {
      // Get the auth session with JWT tokens
      const sessionResult = await auth.getSession();

      if (sessionResult.success && sessionResult.tokens) {
        const idToken = sessionResult.tokens.idToken?.toString();

        if (idToken) {
          // Extract custom claims
          const claims = extractCustomClaims(idToken);

          const userResult = await auth.getCurrentUser();
          if (userResult.success && userResult.user) {
            // Parse JWT to extract organizations and user details
            // GW-374: Extract role from claims.roles (plural, JSON array string)
            // GW-814: Also check cognitoGroups (AWS Cognito standard claim for group membership)
            const roles = parseRoles(claims?.roles);
            const cognitoGroups = claims?.cognitoGroups ?? [];
            const isGlobalAdmin =
              roles.includes('global_admin') || cognitoGroups.includes('global_admin');
            // GW-2322: Extract profile fields from custom JWT claims
            // custom:profileComplete is a string "true"/"false" in Cognito — convert to boolean
            const profileComplete = (claims?.['profileComplete'] as string | undefined) === 'true';
            const user: User = {
              username: userResult.user.username,
              userId: userResult.user.userId,
              email: claims?.email,
              given_name: claims?.given_name,
              family_name: claims?.family_name,
              organizations: [], // Will be populated by fetchUserOrganizations
              role: isGlobalAdmin ? 'global_admin' : undefined,
              givenName: (claims?.['firstName'] as string | undefined) ?? '',
              familyName: (claims?.['lastName'] as string | undefined) ?? '',
              phoneNumber: (claims?.['phoneNumber'] as string | undefined) ?? '',
              companyName: (claims?.['companyName'] as string | undefined) ?? '',
              workState: (claims?.['workState'] as string | undefined) ?? '',
              profileComplete,
              idpSource: (claims?.['idpSource'] as string | undefined) ?? '',
              privyLevel: claims?.privyLevel,
              privyState: claims?.privyState,
            };

            const successState = get();
            const loadedState = setLoaded(
              successState.hasLoaded,
              successState.lastLoaded,
              'signIn',
              true
            );

            // `set` is typed to AuthSlice but operates on the full combined store
            // at runtime. Cast to Record<string, unknown> to allow the cross-slice
            // org resets below (same pattern as the signOut path and
            // organizationSlice.ts line 284).
            (set as (partial: Record<string, unknown>) => void)({
              user,
              isAuthenticated: true,
              isLoading: setLoading(successState.isLoading, 'signIn', false),
              hasLoaded: { ...loadedState.hasLoaded, fetch: false },
              lastLoaded: loadedState.lastLoaded,
              // GW-6372: Clear stale org data from previous session to prevent cross-user contamination
              currentOrg: null,
              userOrganizations: [],
            });
            (get() as unknown as Pick<PrivySlice, 'setPrivyFromAuth'>).setPrivyFromAuth(
              claims?.privyLevel,
              claims?.privyState
            );

            // GW-677: Check for pending invitation code in localStorage
            // This handles the case where user signed up via invitation link
            const pendingInvitationCode = localStorage.getItem(PENDING_INVITATION_CODE_KEY);
            if (pendingInvitationCode) {
              try {
                await organizationsApi.acceptInvitation(pendingInvitationCode);
              } catch (error) {
                // Log but don't fail signIn - invitation acceptance is best-effort
                console.error('GW-677: Failed to accept pending invitation:', error);
              } finally {
                // Always clear the localStorage, even on failure
                localStorage.removeItem(PENDING_INVITATION_CODE_KEY);
              }
            }

            // Note: Organization fetching and selection should be handled
            // by the component after successful sign in
            return true;
          }
        }
      }
    }

    const errorState = get();
    const errorLoadedState = setLoaded(
      errorState.hasLoaded,
      errorState.lastLoaded,
      'signIn',
      false
    );

    // GW-8499: Cognito returns UserNotConfirmedException when the account exists
    // but the email was never verified. Surface a specific, actionable message and
    // expose the attempted email so the login page can offer a resend action.
    // All other failures collapse to the generic message to avoid leaking whether
    // an account exists.
    // `result` is a union; only the failure branch carries `error`, so read it
    // through a narrowing view rather than asserting it exists on every member.
    const errorName = ((result as { error?: { name?: string } }).error ?? undefined)?.name;
    const isUnverified = errorName === 'UserNotConfirmedException';
    const signInMessage = isUnverified
      ? "Your email address hasn't been verified yet — check your inbox for the verification link."
      : 'Invalid username or password';

    set({
      errors: setError(errorState.errors, 'signIn', signInMessage),
      isLoading: setLoading(errorState.isLoading, 'signIn', false),
      hasLoaded: errorLoadedState.hasLoaded,
      lastLoaded: errorLoadedState.lastLoaded,
      isAuthenticated: false,
      unverifiedEmail: isUnverified ? username : null,
    });
    return false;
  },

  signOut: async () => {
    // GW-9525: Invalidate any in-flight `fetchUserOrganizations` as the FIRST
    // synchronous side effect — BEFORE the `auth.signOut()` await below — so a
    // fetch that resolves during the await window is also invalidated and does
    // not re-mirror a stale `gw-current-org-id` back into localStorage after we
    // clear it. Incrementing after the await would leave that window racy.
    invalidateOrgFetches();

    const currentState = get();

    // GW-9525 (cycle 3): Clear org id + reset auth/org state SYNCHRONOUSLY,
    // BEFORE the `auth.signOut()` await below — not after it. Cycles 1 & 2
    // reset only after the await, which left `isAuthenticated` TRUE and
    // `gw-current-org-id` present throughout the await window. A
    // `fetchUserOrganizations` resolving DURING that window (capturing the
    // post-`invalidateOrgFetches` generation, so organizationSlice's
    // generation guard does not catch it) then passed the `isAuthenticated`
    // mirror-write guard and resurrected the stale org id. Setting
    // `isAuthenticated: false` (and clearing the org id) up front makes those
    // guards correctly bail for a fetch resolving during the await AND a fetch
    // initiated after signOut. `invalidateOrgFetches()` above and
    // organizationSlice's guards are RETAINED as defense in depth.
    //
    // GW-349: Fix logout not clearing organization state
    // GW-409: Fix logout not clearing token balance and other cached data
    // GW-XXX: After logout, mark checkAuth as loaded so PublicRoute doesn't
    // show infinite loading spinner when navigating to /auth/login.
    // `set` is typed to AuthSlice but operates on the full combined store at runtime.
    // Cast to Record<string, unknown> to allow cross-slice field resets (same pattern
    // as organizationSlice.ts line 284).
    localStorage.removeItem('gw-current-org-id');
    (set as (partial: Record<string, unknown>) => void)({
      // Auth state reset (with checkAuth marked as loaded). `isLoading` is
      // re-set below to reflect the in-flight signOut operation.
      ...initialAuthState,
      hasLoaded: { checkAuth: true }, // User is verified as unauthenticated
      isLoading: setLoading(currentState.isLoading, 'signOut', true),
      // Organization state reset (both slices share the same store)
      currentOrg: null,
      userOrganizations: [],
      usageMetrics: null,
      members: [],
      invitations: [],
      // GW-409: Token balance reset (tokensSlice)
      tokenBalance: null,
      // GW-409: Organization tokens reset (organizationTokensSlice)
      organizationBalance: null,
      organizationTransactions: [],
      organizationTransactionPagination: null,
      organizationSubscription: null,
      // GW-409: User transactions reset (userTransactionsSlice)
      userTransactions: [],
      userTokenBalance: null,
      userPagination: { total: 0, limit: 50, offset: 0, hasMore: false },
      userFilters: {},
      userSort: { field: 'createdAt', order: 'desc' },
      userIsLoading: {},
      userHasLoaded: {},
      userLastLoaded: {},
      userErrors: {},
    });

    // Sign out of Cognito AFTER the local state has been durably cleared. If
    // this throws, the local session has already been reset (the desired
    // durability — a failed provider signOut must not leave the app looking
    // signed in), so we let any error propagate without restoring state.
    await auth.signOut();

    // Mark the signOut operation as no longer loading now that it has
    // completed. Re-read state because the fetch guards / other actions may
    // have run during the await.
    const finalState = get();
    set({ isLoading: setLoading(finalState.isLoading, 'signOut', false) });
  },

  signUp: async (username: string, password: string, email: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'signUp', true),
      errors: clearOpError(currentState.errors, 'signUp'),
      signUpRecovery: null, // GW-749: Clear previous recovery state
    });

    const result = await auth.signUp(username, password, email);

    const state = get();
    if (result.success) {
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'signUp', true);
      set({
        isLoading: setLoading(state.isLoading, 'signUp', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        signUpRecovery: null, // GW-749: Clear recovery on success
      });
      return true;
    }

    // GW-749: Use specific error message from Cognito error parsing
    const errorMessage = result.userMessage || 'Sign up failed. Please try again.';

    // GW-749: Set recovery info if user can resend verification
    const signUpRecovery = result.canResendVerification
      ? { canResendVerification: true, email }
      : null;

    const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'signUp', false);
    set({
      errors: setError(state.errors, 'signUp', errorMessage),
      isLoading: setLoading(state.isLoading, 'signUp', false),
      hasLoaded: loadedState.hasLoaded,
      lastLoaded: loadedState.lastLoaded,
      signUpRecovery, // GW-749: Expose recovery info to UI
    });
    return false;
  },

  confirmSignUp: async (username: string, code: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'confirmSignUp', true),
      errors: clearOpError(currentState.errors, 'confirmSignUp'),
    });

    const result = await auth.confirmSignUp(username, code);

    const state = get();
    if (result.success && result.isSignUpComplete) {
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'confirmSignUp', true);
      set({
        isLoading: setLoading(state.isLoading, 'confirmSignUp', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      return true;
    }

    // GW-1294: Handle already-confirmed user - treat as success so UI navigates to login
    // User IS verified, they just tried to verify again (e.g., expired code then already confirmed)
    if ('isAlreadyConfirmed' in result && result.isAlreadyConfirmed) {
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'confirmSignUp', true);
      set({
        isLoading: setLoading(state.isLoading, 'confirmSignUp', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        // No error - user is verified, redirect them to login
      });
      return true;
    }

    // GW-1294: Use specific error message from Cognito error parsing (same pattern as signUp)
    const errorMessage = result.userMessage || 'Verification failed. Please try again.';

    const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'confirmSignUp', false);
    set({
      errors: setError(state.errors, 'confirmSignUp', errorMessage),
      isLoading: setLoading(state.isLoading, 'confirmSignUp', false),
      hasLoaded: loadedState.hasLoaded,
      lastLoaded: loadedState.lastLoaded,
    });
    return false;
  },

  resendSignUpCode: async (username: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'resendSignUpCode', true),
      errors: clearOpError(currentState.errors, 'resendSignUpCode'),
    });

    const result = await auth.resendSignUpCode(username);

    const state = get();
    if (result.success) {
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'resendSignUpCode', true);
      set({
        isLoading: setLoading(state.isLoading, 'resendSignUpCode', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
      return true;
    }

    // GW-805: Handle already-confirmed user - return structured response, don't set error
    // This is informational, not an error - user should be redirected to login
    if ('isAlreadyConfirmed' in result && result.isAlreadyConfirmed) {
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'resendSignUpCode', true);
      set({
        isLoading: setLoading(state.isLoading, 'resendSignUpCode', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        // No error - this is expected behavior for already-confirmed users
      });
      return { success: false, isAlreadyConfirmed: true };
    }

    const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'resendSignUpCode', false);
    set({
      errors: setError(state.errors, 'resendSignUpCode', 'Failed to resend code'),
      isLoading: setLoading(state.isLoading, 'resendSignUpCode', false),
      hasLoaded: loadedState.hasLoaded,
      lastLoaded: loadedState.lastLoaded,
    });
    return false;
  },

  // GW-807: Initiate password reset - sends verification code to email
  forgotPassword: async (email: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'forgotPassword', true),
      errors: clearOpError(currentState.errors, 'forgotPassword'),
      passwordResetEmail: null,
    });

    const result = await auth.forgotPassword(email);

    const state = get();
    if (result.success) {
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'forgotPassword', true);
      set({
        isLoading: setLoading(state.isLoading, 'forgotPassword', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        passwordResetEmail: email, // Store email for the confirmation step
      });
      return true;
    }

    const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'forgotPassword', false);
    set({
      errors: setError(
        state.errors,
        'forgotPassword',
        result.userMessage || 'Failed to send reset code'
      ),
      isLoading: setLoading(state.isLoading, 'forgotPassword', false),
      hasLoaded: loadedState.hasLoaded,
      lastLoaded: loadedState.lastLoaded,
    });
    return false;
  },

  // GW-807: Complete password reset with verification code and new password
  confirmForgotPassword: async (email: string, code: string, newPassword: string) => {
    const currentState = get();
    set({
      isLoading: setLoading(currentState.isLoading, 'confirmForgotPassword', true),
      errors: clearOpError(currentState.errors, 'confirmForgotPassword'),
    });

    const result = await auth.confirmForgotPassword(email, code, newPassword);

    const state = get();
    if (result.success) {
      const loadedState = setLoaded(
        state.hasLoaded,
        state.lastLoaded,
        'confirmForgotPassword',
        true
      );
      set({
        isLoading: setLoading(state.isLoading, 'confirmForgotPassword', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        passwordResetEmail: null, // Clear after successful reset
      });
      return true;
    }

    const loadedState = setLoaded(
      state.hasLoaded,
      state.lastLoaded,
      'confirmForgotPassword',
      false
    );
    set({
      errors: setError(
        state.errors,
        'confirmForgotPassword',
        result.userMessage || 'Failed to reset password'
      ),
      isLoading: setLoading(state.isLoading, 'confirmForgotPassword', false),
      hasLoaded: loadedState.hasLoaded,
      lastLoaded: loadedState.lastLoaded,
    });
    return false;
  },

  checkAuth: async () => {
    const currentState = get();

    // Guard: Don't check again if already loading
    // GW-444: Removed early return when isAuthenticated is true
    // Must ALWAYS verify with Cognito to prevent stale localStorage state issues
    if (isOperationLoading(currentState.isLoading, 'checkAuth')) {
      return;
    }
    set({ isLoading: setLoading(currentState.isLoading, 'checkAuth', true) });

    try {
      const isAuth = await auth.isAuthenticated();

      if (isAuth) {
        const userResult = await auth.getCurrentUser();

        if (userResult.success && userResult.user) {
          // GW-459: Extract JWT claims to get role, email, and name fields
          // Same pattern as signIn to ensure consistent user data
          const sessionResult = await auth.getSession();
          let claims = null;
          let roles: string[] = [];

          if (sessionResult.success && sessionResult.tokens) {
            const idToken = sessionResult.tokens.idToken?.toString();
            if (idToken) {
              claims = extractCustomClaims(idToken);
              roles = parseRoles(claims?.roles);
            }
          }

          // GW-814: Also check cognitoGroups (AWS Cognito standard claim for group membership)
          const cognitoGroups = claims?.cognitoGroups ?? [];
          const isGlobalAdmin =
            roles.includes('global_admin') || cognitoGroups.includes('global_admin');
          // GW-2322: Extract profile fields from custom JWT claims
          // custom:profileComplete is a string "true"/"false" in Cognito — convert to boolean
          const profileComplete = (claims?.['profileComplete'] as string | undefined) === 'true';

          const state = get();
          const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'checkAuth', true);

          set({
            user: {
              username: userResult.user.username,
              userId: userResult.user.userId,
              email: claims?.email,
              given_name: claims?.given_name,
              family_name: claims?.family_name,
              organizations: [], // Will be populated by fetchUserOrganizations
              role: isGlobalAdmin ? 'global_admin' : undefined,
              givenName: (claims?.['firstName'] as string | undefined) ?? '',
              familyName: (claims?.['lastName'] as string | undefined) ?? '',
              phoneNumber: (claims?.['phoneNumber'] as string | undefined) ?? '',
              companyName: (claims?.['companyName'] as string | undefined) ?? '',
              workState: (claims?.['workState'] as string | undefined) ?? '',
              profileComplete,
              idpSource: (claims?.['idpSource'] as string | undefined) ?? '',
              privyLevel: claims?.privyLevel,
              privyState: claims?.privyState,
            },
            isAuthenticated: true,
            isLoading: setLoading(state.isLoading, 'checkAuth', false),
            hasLoaded: loadedState.hasLoaded,
            lastLoaded: loadedState.lastLoaded,
          });
          (get() as unknown as Pick<PrivySlice, 'setPrivyFromAuth'>).setPrivyFromAuth(
            claims?.privyLevel,
            claims?.privyState
          );
          return;
        }
      }

      const state = get();
      // GW-484: The 'success' parameter means "operation completed", not "user is authenticated"
      // Setting false here was causing PublicRoute to wait forever on unauthenticated users
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'checkAuth', true);

      set({
        user: null,
        isAuthenticated: false,
        isLoading: setLoading(state.isLoading, 'checkAuth', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    } catch (error) {
      // GW-12871 (Sentry GW-SPA-39): auth.isAuthenticated() calls getConfig()
      // as its first statement, which THROWS when window.__RUNTIME_CONFIG__ is
      // transiently absent after a deploy (env-config.js failed to load). Since
      // App.tsx fires checkAuth() UN-AWAITED with no .catch, an escaping throw
      // becomes an unhandled promise rejection on every load. Swallow it, log
      // it (mirroring the graceful degradation in awsRum.util.ts), and degrade
      // to the SAME terminal logged-out state as the normal unauthenticated
      // path above so checkAuth() always resolves.
      console.error(
        '[checkAuth] Runtime config unavailable; degrading to logged-out state:',
        error
      );

      const state = get();
      const loadedState = setLoaded(state.hasLoaded, state.lastLoaded, 'checkAuth', true);

      set({
        user: null,
        isAuthenticated: false,
        isLoading: setLoading(state.isLoading, 'checkAuth', false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
      });
    }
  },

  clearError: (operation?: string) => {
    if (operation) {
      // GW-749: Clear signUpRecovery when clearing signUp error
      if (operation === 'signUp') {
        set((state) => ({
          ...state,
          errors: clearOpError(state.errors, operation),
          signUpRecovery: null,
        }));
      } else if (operation === 'signIn') {
        // GW-8499: Clear unverifiedEmail when clearing signIn error so the
        // resend action does not linger after the user edits/re-submits.
        set((state) => ({
          ...state,
          errors: clearOpError(state.errors, operation),
          unverifiedEmail: null,
        }));
      } else {
        set((state) => ({
          ...state,
          errors: clearOpError(state.errors, operation),
        }));
      }
    } else {
      set((state) => ({
        ...state,
        errors: {},
        signUpRecovery: null,
        unverifiedEmail: null,
      }));
    }
  },

  // GW-749: Unique function to clear signUp error AND signUpRecovery
  // This avoids slice collision since other slices also define clearError
  clearSignUpError: () => {
    set((state) => ({
      ...state,
      errors: clearOpError(state.errors, 'signUp'),
      signUpRecovery: null,
    }));
  },

  // GW-8499: Clear the unverified-email recovery state (mirrors clearSignUpError)
  clearUnverifiedEmail: () => {
    set((state) => ({
      ...state,
      unverifiedEmail: null,
    }));
  },

  setNeedsOrgSelection: (needs: boolean) => {
    set({ needsOrgSelection: needs });
  },

  // GW-1709: Update user profile fields in store
  updateUser: (updates) => {
    set((state) => ({
      user: state.user ? { ...state.user, ...updates } : null,
    }));
  },

  // GW-2338: Force a Cognito token refresh and sync fresh profile claims to the store
  refreshUserFromToken: async () => {
    const sessionResult = await auth.refreshSession();
    if (!sessionResult.success || !sessionResult.tokens) return;

    const idToken = sessionResult.tokens.idToken?.toString();
    if (!idToken) return;

    const claims = extractCustomClaims(idToken);
    set((state) => ({
      user: state.user
        ? {
            ...state.user,
            givenName: (claims?.['firstName'] as string | undefined) ?? state.user.givenName,
            familyName: (claims?.['lastName'] as string | undefined) ?? state.user.familyName,
            // GW-13389: phoneNumber and companyName are encrypted at rest in Cognito and
            // cannot be safely read from JWT claims — the pre-token-generation Lambda may
            // return ciphertext when KMS decryption fails. These fields must never be
            // overwritten from JWT claims; the plaintext written by updateUser() is authoritative.
            workState: (claims?.['workState'] as string | undefined) ?? state.user.workState,
            profileComplete: (claims?.['profileComplete'] as string | undefined) === 'true',
            privyLevel: claims?.privyLevel ?? state.user.privyLevel,
            privyState: claims?.privyState ?? state.user.privyState,
          }
        : null,
    }));
    (get() as unknown as Pick<PrivySlice, 'setPrivyFromAuth'>).setPrivyFromAuth(
      claims?.privyLevel,
      claims?.privyState
    );
  },
});
